import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import LegalFootnote from './components/LegalFootnote';
import './portal-v2.css';

const PortalV2Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    email: '', password: '', confirm_password: '',
    first_name: '', last_name: '', company_name: '', phone: '',
    referral_code: '',
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirm_password) {
      return setError('Passwords do not match');
    }
    setLoading(true);
    const result = await register(formData);
    setLoading(false);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => navigate('/portal/login'), 1800);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="portal-v2">
      <div className="pv2-login-shell">
        <div className="pv2-login-bg" style={{ backgroundImage: 'url(/lubbock-coverage.jpg)' }} aria-hidden="true" />
        <div className="pv2-login-card" data-testid="pv2-register-card" style={{ maxWidth: 540 }}>
          <div className="pv2-mono-tag" style={{ marginBottom: 14 }}>Create Account · WesTex Legal</div>
          <h1 className="pv2-display" style={{ fontSize: 36, marginBottom: 8 }}>
            Request <span className="pv2-display-italic">access.</span>
          </h1>
          <p style={{ color: 'var(--pv2-text-muted)', fontSize: 13, marginBottom: 24 }}>
            Your email domain must match an approved firm. We'll review and activate within one business day.
          </p>

          {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}
          {success && <div className="pv2-alert pv2-alert-green"><CheckCircle size={14} /><span>Account created. Redirecting…</span></div>}

          {!success && (
            <form onSubmit={handleSubmit} data-testid="pv2-register-form">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="pv2-field">
                  <label className="pv2-label">First name</label>
                  <input className="pv2-input" name="first_name" required value={formData.first_name} onChange={handleChange} data-testid="pv2-reg-first" />
                </div>
                <div className="pv2-field">
                  <label className="pv2-label">Last name</label>
                  <input className="pv2-input" name="last_name" required value={formData.last_name} onChange={handleChange} data-testid="pv2-reg-last" />
                </div>
              </div>
              <div className="pv2-field">
                <label className="pv2-label">Email</label>
                <input className="pv2-input" name="email" type="email" required value={formData.email} onChange={handleChange} data-testid="pv2-reg-email" />
              </div>
              <div className="pv2-field">
                <label className="pv2-label">Company / Firm</label>
                <input className="pv2-input" name="company_name" required value={formData.company_name} onChange={handleChange} data-testid="pv2-reg-company" />
              </div>
              <div className="pv2-field">
                <label className="pv2-label">Phone</label>
                <input className="pv2-input" name="phone" required value={formData.phone} onChange={handleChange} data-testid="pv2-reg-phone" />
              </div>
              <div className="pv2-field">
                <label className="pv2-label">Password</label>
                <div style={{ position: 'relative' }}>
                  <input className="pv2-input" name="password" type={showPwd ? 'text' : 'password'} required minLength={8} value={formData.password} onChange={handleChange} style={{ paddingRight: 44 }} data-testid="pv2-reg-pwd" />
                  <button type="button" onClick={() => setShowPwd(s => !s)} aria-label="Toggle password" style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 44, background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}>
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="pv2-field">
                <label className="pv2-label">Confirm password</label>
                <div style={{ position: 'relative' }}>
                  <input className="pv2-input" name="confirm_password" type={showConfirm ? 'text' : 'password'} required value={formData.confirm_password} onChange={handleChange} style={{ paddingRight: 44 }} data-testid="pv2-reg-confirm" />
                  <button type="button" onClick={() => setShowConfirm(s => !s)} aria-label="Toggle password" style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 44, background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}>
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="pv2-field">
                <label className="pv2-label">Referral code <span style={{ opacity: 0.5 }}>(optional)</span></label>
                <input className="pv2-input" name="referral_code" value={formData.referral_code} onChange={handleChange} placeholder="e.g. AB12CD" data-testid="pv2-reg-ref" />
              </div>
              <button type="submit" disabled={loading} className="pv2-btn pv2-btn-primary" style={{ width: '100%' }} data-testid="pv2-reg-submit">
                {loading ? 'Creating…' : 'Create Account'}
              </button>

              <LegalFootnote action="creating an account" testId="pv2-reg-legal" />
            </form>
          )}

          <div style={{ marginTop: 24, fontSize: 12 }}>
            <span style={{ color: 'var(--pv2-text-muted)' }}>Already registered?</span>{' '}
            <Link to="/portal/login" className="pv2-mono" style={{ color: 'var(--pv2-red-bright)' }}>Sign in</Link>
          </div>
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--pv2-ink-line)' }}>
            <Link to="/" className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--pv2-text-subtle)' }}>
              <ArrowLeft size={12} /> Back to website
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalV2Register;
